// Phase-caption logic for the Studio "Building your deck" loading animation.
//
// While a generation is in flight the canvas shows an animated skeleton deck
// rather than the pre-theme picker. The caption underneath reflects where the
// stream has got to, derived from the counts the studio already tracks PLUS the
// latest server-emitted status caption (so the pre-outline phase no longer sits
// frozen on "Planning the outline…" while the slow grounding + research runs):
//
//   • before any outline lands  → the server's latest status caption
//     ("Reading your document…" / "Researching…" / "Planning the outline…"),
//     falling back to "Planning the outline…" when none has arrived yet
//   • once the outline + slides stream → "Designing your slides…" + the title of
//     the slide currently landing (else "Slide N of M")
//   • all slides filled, stream still closing → "Polishing your slides…"
//
// This is deliberately PURE (no DOM, no component state) so it is trivially
// unit-testable and the studio just feeds it {total, filled, serverCaption?,
// currentTitle?}. British spelling throughout. No emojis.

/** Inputs the studio can supply directly from its stream state. */
export interface BuildingPhaseInput {
  /** Total slides the outline declared (deck.slides.length). 0 before outline. */
  total: number;
  /** Slides whose generated content has arrived (total − pending.size). */
  filled: number;
  /** Latest server status caption (pre-outline phase only). Empty/absent →
   *  the default "Planning the outline…". */
  serverCaption?: string;
  /** Title of the slide currently landing — drives the streaming detail line
   *  ("'Citation Authority'") in place of the "Slide N of M" fallback. */
  currentTitle?: string;
}

/** The two lines shown under the building animation. `detail` is empty when
 *  there is nothing useful to count yet (the pre-outline phase). */
export interface BuildingPhase {
  /** Coarse phase id — drives the progress fraction + any per-phase styling. */
  phase: 'planning' | 'designing' | 'polishing';
  /** Primary caption line. */
  caption: string;
  /** Secondary line, e.g. "Slide 2 of 6". Empty while planning. */
  detail: string;
  /** Progress 0..1 for the soft progress line. */
  progress: number;
}

/** Derive the building phase from the stream counts. Clamps defensively so a
 *  stray over-count (filled > total) or negative never produces a daft caption
 *  or an out-of-range progress value. */
export function buildingPhase(input: BuildingPhaseInput): BuildingPhase {
  const total = Math.max(0, Math.floor(input.total) || 0);
  const filled = Math.min(total, Math.max(0, Math.floor(input.filled) || 0));
  const serverCaption = (input.serverCaption || '').trim();
  const currentTitle = (input.currentTitle || '').trim();

  // Pre-outline: the model is still grounding / researching / planning the
  // structure — nothing to count. Show the latest server status caption when
  // one has arrived, else the default so the caption is never blank. The `phase`
  // stays 'planning' so the progress bar's indeterminate styling is unchanged.
  if (total === 0) {
    return {
      phase: 'planning',
      caption: serverCaption || 'Planning the outline…',
      detail: '',
      progress: 0,
    };
  }

  // Every slide has landed but the stream hasn't closed yet — a brief tail.
  if (filled >= total) {
    return { phase: 'polishing', caption: 'Polishing your slides…', detail: `${total} slides`, progress: 1 };
  }

  // Streaming: prefer the title of the slide currently landing; otherwise count
  // the slide being designed (next one, 1-based, capped so it never reads
  // "Slide 7 of 6").
  const current = Math.min(total, filled + 1);
  return {
    phase: 'designing',
    caption: 'Designing your slides…',
    detail: currentTitle ? `‘${currentTitle}’` : `Slide ${current} of ${total}`,
    progress: filled / total,
  };
}
